import { ConfigurationSelector } from '@ds-mgnl/marketing';
import { SelectInput, Text } from '@dsx/react';
import { useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { FamilyAssetMap } from '../../../services/productApi';
import { getCachedProduct, getProductById } from '../../../services/productApi';
import { chipUrl } from '../chipUrl';
import { slugify } from '../slugify';
import styles from './FamilyVariantPicker.module.css';

interface DimensionModel {
	slug: string;
	display: string;
	// * `orphaned` = no per-value entry in `assets.dimensions` for this
	//   dimension. Render as a dropdown instead of a swatch strip since we
	//   have no images or curated ordering to justify a visual grid.
	orphaned: boolean;
	values: { slug: string; display: string; chipUrl: string | undefined }[];
}

interface VariantSelection {
	dim: DimensionModel;
	slug: string;
}

function slugsEqual(
	a: Record<string, string>,
	b: Record<string, string>,
): boolean {
	const aKeys = Object.keys(a);
	if (aKeys.length !== Object.keys(b).length) return false;
	for (const k of aKeys) {
		if (a[k] !== b[k]) return false;
	}
	return true;
}

function findProductBySlugs(
	products: Record<string, Record<string, string>>,
	target: Record<string, string>,
): string | undefined {
	for (const [productId, slugs] of Object.entries(products)) {
		if (slugsEqual(slugs, target)) return productId;
	}
	return undefined;
}

// * Build the picker model from all three sections of the family asset map.
//   - `products` is authoritative for which value slugs exist per dimension.
//   - `variants` gives display strings + preferred ordering.
//   - `assets.dimensions` provides canonical display + swatch image (may be a
//     subset — coverage gaps are expected).
function buildDimensionModels(map: FamilyAssetMap): DimensionModel[] {
	const dimSlugToValues = new Map<string, Set<string>>();
	for (const slugs of Object.values(map.products)) {
		for (const [dimSlug, valSlug] of Object.entries(slugs)) {
			if (!dimSlugToValues.has(dimSlug)) {
				dimSlugToValues.set(dimSlug, new Set());
			}
			dimSlugToValues.get(dimSlug)!.add(valSlug);
		}
	}

	const variantsBySlug = new Map<
		string,
		{ display: string; values: string[] }
	>();
	for (const [dimDisplay, valDisplays] of Object.entries(map.variants ?? {})) {
		variantsBySlug.set(slugify(dimDisplay), {
			display: dimDisplay,
			values: valDisplays,
		});
	}

	const models: DimensionModel[] = [];

	for (const [dimSlug, valSlugSet] of dimSlugToValues) {
		const assetDim = map.assets?.dimensions?.[dimSlug];
		const variantEntry = variantsBySlug.get(dimSlug);
		const display = assetDim?.display ?? variantEntry?.display ?? dimSlug;

		const ordered: string[] = [];
		const seen = new Set<string>();
		for (const valDisplay of variantEntry?.values ?? []) {
			const valSlug = slugify(valDisplay);
			if (valSlugSet.has(valSlug) && !seen.has(valSlug)) {
				ordered.push(valSlug);
				seen.add(valSlug);
			}
		}
		for (const valSlug of valSlugSet) {
			if (!seen.has(valSlug)) {
				ordered.push(valSlug);
				seen.add(valSlug);
			}
		}

		const displayByVariantSlug = new Map<string, string>();
		for (const valDisplay of variantEntry?.values ?? []) {
			displayByVariantSlug.set(slugify(valDisplay), valDisplay);
		}

		const assetValueCount = assetDim?.values
			? Object.keys(assetDim.values).length
			: 0;
		const orphaned = assetValueCount === 0;

		const values = ordered.map((valSlug) => {
			const assetVal = assetDim?.values?.[valSlug];
			return {
				slug: valSlug,
				display:
					assetVal?.display ?? displayByVariantSlug.get(valSlug) ?? valSlug,
				chipUrl: chipUrl(assetVal),
			};
		});

		models.push({ slug: dimSlug, display, orphaned, values });
	}

	return models;
}

export interface ProductVariantSelection {
	productId: string;
	optionName: string;
	optionValue: string;
}

export interface FamilyVariantPickerProps {
	map: FamilyAssetMap;
	currentProductId: string;
	loading?: boolean;
	onSelect: (selection: ProductVariantSelection) => Promise<void>;
}

export function FamilyVariantPicker({
	map,
	currentProductId,
	loading = false,
	onSelect,
}: FamilyVariantPickerProps) {
	const navigate = useNavigate();

	const location = useLocation();

	const currentSlugs = map.products[currentProductId];
	const dimensions = useMemo(() => buildDimensionModels(map), [map]);

	// * Debounced hover-prefetch: warm the target product's cache ~150ms after
	//   the pointer settles on a swatch so navigation is instant, without firing
	//   a request for every swatch the pointer skims past.
	const prefetchTimer = useRef<ReturnType<typeof setTimeout>>();
	const cancelPrefetch = () => {
		if (prefetchTimer.current) {
			clearTimeout(prefetchTimer.current);
			prefetchTimer.current = undefined;
		}
	};
	useEffect(() => cancelPrefetch, []);

	if (!currentSlugs) return null;
	if (dimensions.length === 0) return null;

	// * Availability (exact match): true only when a single SKU carries the
	//   picked value alongside EVERY other current selection. Drives the
	//   greyed / "Not available" labeling — never blocks the click.
	const isExactAvailable = (dimSlug: string, valSlug: string): boolean =>
		!!findProductBySlugs(map.products, { ...currentSlugs, [dimSlug]: valSlug });

	const findBestMatch = (
		dimSlug: string,
		valSlug: string,
	): string | undefined => {
		let best: string | undefined;
		let bestScore = -1;
		for (const [productId, slugs] of Object.entries(map.products)) {
			if (slugs[dimSlug] !== valSlug) continue;
			let score = 0;
			for (const [k, v] of Object.entries(currentSlugs)) {
				if (k === dimSlug) continue;
				if (slugs[k] === v) score++;
			}
			if (score > bestScore) {
				bestScore = score;
				best = productId;
			}
		}
		return best;
	};

	const schedulePrefetch = (dimSlug: string, valSlug: string) => {
		cancelPrefetch();
		const target = findBestMatch(dimSlug, valSlug);
		// * Nothing to warm if there's no target, it's the current product, or
		//   it's already cached.
		if (!target || target === currentProductId || getCachedProduct(target)) {
			return;
		}
		prefetchTimer.current = setTimeout(() => {
			// * Fire-and-forget; getProductById caches on success. Swallow errors —
			//   navigation will re-request and surface any failure then.
			void getProductById(target).catch(() => {});
		}, 150);
	};

	const handleSelect = async ({ dim, slug }: VariantSelection) => {
		const nextProductId = findBestMatch(dim.slug, slug);
		if (!nextProductId || nextProductId === currentProductId) return;

		try {
			await getProductById(nextProductId);
			await onSelect({
				productId: nextProductId,
				optionName: dim.display,
				optionValue: slug,
			});
		} catch {
			// * Fall through to navigation; the target PDP will surface the error.
		}
		// * Preserve the current query string (e.g. a display-only `?fabric=`
		//   preview selection) so it survives navigation to the sibling product.
		navigate(`/product/${nextProductId}${location.search}`, {
			state: { preserveScroll: true, viaConfigurator: true },
		});
	};

	return (
		<div className={styles['picker']}>
			{dimensions.map((dim) => {
				const currentValSlug = currentSlugs[dim.slug];

				if (dim.orphaned) {
					const currentDisplay = dim.values.find(
						(v) => v.slug === currentValSlug,
					)?.display;
					return (
						<div key={dim.slug} className={styles['dimension']}>
							<div className={styles['dimension-header']}>
								<Text variant="body-bold">{dim.display}</Text>
								{currentDisplay && (
									<Text variant="body" className={styles['dimension-value']}>
										{currentDisplay}
									</Text>
								)}
							</div>
							<SelectInput
								options={dim.values.map((value) => {
									// * Every option stays selectable (the user takes priority and
									//   can pick anything). Options that don't map to an exact SKU
									//   with the current selections are labeled "(Not available)"
									//   rather than disabled — clicking still navigates to the
									//   closest sibling.
									const available =
										value.slug === currentValSlug ||
										isExactAvailable(dim.slug, value.slug);
									return {
										label: available
											? value.display
											: `${value.display} (Not available)`,
										value: value.slug,
									};
								})}
								value={currentValSlug ?? ''}
								onChange={(e) => {
									const nextValSlug = e.target.value;
									const nextProductId = findBestMatch(dim.slug, nextValSlug);
									if (nextProductId) {
										void handleSelect({ dim, slug: nextValSlug });
									}
								}}
							/>
						</div>
					);
				}

				const items = dim.values.map((value) => {
					const isSelected = currentValSlug === value.slug;
					const available =
						isSelected || isExactAvailable(dim.slug, value.slug);
					return {
						id: value.slug,
						label: available
							? value.display
							: `${value.display} (Not available)`,
						imageProps: value.chipUrl
							? // crossOrigin forces the thumbnail into cors mode so it shares
							  // the same cache entry as the model-viewer's texture fetch for
							  // this presigned URL. Cast past DSX's AcceptedImgAttributes
							  // whitelist; the attr is spread onto the <img> at runtime.
							  ({
									src: value.chipUrl,
									alt: value.display,
									crossOrigin: 'anonymous',
								} as { src: string; alt: string })
							: undefined,
						cardClassName: available ? undefined : styles['unavailable'],
					};
				});

				return (
					<ConfigurationSelector
						key={dim.slug}
						selectorType={dim.display}
						items={items}
						value={currentValSlug}
						loading={loading}
						onChange={(nextSlug: string) => {
							handleSelect({ dim, slug: nextSlug });
						}}
						onItemHover={(itemId: string) => schedulePrefetch(dim.slug, itemId)}
						onItemHoverEnd={cancelPrefetch}
					/>
				);
			})}
		</div>
	);
}
